import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Statistic, Spin, message, Typography, Space, Button, Tag, List, Progress, Empty } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import api from '../api/client';

/**
 * Dashboard — сводка по SLA и привязкам
 */
function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtShortDate(tsSec) {
  if (!tsSec) return '';
  const d = new Date(Number(tsSec) * 1000);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const kpi = useMemo(() => {
    const s = stats || {};
    const pending = safeNum(s.sla_pending);
    const inProgress = safeNum(s.sla_in_progress);
    const answered = safeNum(s.sla_answered);
    const alerted = safeNum((s.sla_alerted ?? s.sla_overdue) ?? 0); // опционально
    const bindingsTotal = safeNum(s.bindings_total);
    return { pending, inProgress, answered, alerted, bindingsTotal };
  }, [stats]);

  const perStar = useMemo(() => {
    const s = stats || {};
    const arr = Array.isArray(s.per_star) ? s.per_star : [];
    return arr
      .map((x) => ({
        star_level: x.star_level ?? x.level ?? x.star ?? null,
        total: safeNum(x.total ?? x.bindings_total ?? x.count),
        pending: safeNum(x.pending ?? x.sla_pending),
        alerted: safeNum(x.alerted ?? x.sla_alerted ?? x.overdue ?? x.sla_overdue),
      }))
      .filter((x) => x.star_level != null)
      .sort((a, b) => Number(a.star_level) - Number(b.star_level));
  }, [stats]);

  const recentAlerts = useMemo(() => {
    const s = stats || {};
    const arr = Array.isArray(s.recent_alerts) ? s.recent_alerts : [];
    return arr.slice(0, 6);
  }, [stats]);

  async function load({ silent = false } = {}) {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);

      const res = await api.getDashboard();
      setStats(res?.data ?? res);
    } catch (e) {
      console.error(e);
      message.error('Не удалось загрузить дашборд');
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Spin style={{ marginTop: 40 }} />
      </div>
    );
  }

  if (!stats) {
    console.warn('Dashboard stats empty:', stats);
    return (
      <div style={{ padding: 16 }}>
        <Card>
          <Empty description="Нет данных (API вернул пустой ответ)" />
          <div style={{ marginTop: 12 }}>
            <Button icon={<ReloadOutlined />} onClick={() => load()}>
              Обновить
            </Button>
          </div>
        </Card>
      </div>
    );
  }


  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Card bodyStyle={{ padding: 14 }}>
          <Row align="middle" justify="space-between" gutter={[12, 12]}>
            <Col flex="auto">
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                PB Star Bot
              </Typography.Text>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Дашборд
              </Typography.Title>
            </Col>
            <Col>
              <Button
                icon={<ReloadOutlined />}
                loading={refreshing}
                onClick={() => load({ silent: true })}
              >
                Обновить
              </Button>
            </Col>
          </Row>
        </Card>

        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic title="Ожидают ответа" value={kpi.pending} />
              <div style={{ marginTop: 8 }}>
                <Tag color="gold">pending</Tag>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic title="Просрочки" value={kpi.alerted} />
              <div style={{ marginTop: 8 }}>
                <Tag color="red">alerted</Tag>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic title="В работе" value={kpi.inProgress} />
              <div style={{ marginTop: 8 }}>
                <Tag color="blue">in_progress</Tag>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic title="Привязанных чатов" value={kpi.bindingsTotal} />
              <div style={{ marginTop: 8 }}>
                <Tag color="geekblue">bindings</Tag>
              </div>
            </Card>
          </Col>
        </Row>

        <Row gutter={[12, 12]}>
          <Col xs={24} lg={12}>
            <Card title="По звёздам" extra={<Typography.Text type="secondary">привязки и нагрузка</Typography.Text>}>
              {perStar.length === 0 ? (
                <Empty description="Пока нет данных по звёздам" />
              ) : (
                <List
                  dataSource={perStar}
                  renderItem={(item) => {
                    const total = Math.max(0, item.total);
                    const pending = Math.max(0, item.pending);
                    const alerted = Math.max(0, item.alerted);
                    const pressure = total ? Math.min(100, Math.round(((pending + alerted) / total) * 100)) : 0;

                    return (
                      <List.Item>
                        <List.Item.Meta
                          title={
                            <Space size={10}>
                              <Typography.Text strong>⭐ {item.star_level}</Typography.Text>
                              <Typography.Text type="secondary">привязок: {total}</Typography.Text>
                            </Space>
                          }
                          description={
                            <Space size={8} wrap>
                              <Tag color="gold">pending: {pending}</Tag>
                              <Tag color="red">alerted: {alerted}</Tag>
                            </Space>
                          }
                        />
                        <div style={{ width: 140 }}>
                          <Progress percent={pressure} size="small" />
                        </div>
                      </List.Item>
                    );
                  }}
                />
              )}
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Последние алерты" extra={<Typography.Text type="secondary">последние 6</Typography.Text>}>
              {recentAlerts.length === 0 ? (
                <Empty description="Алертов пока нет" />
              ) : (
                <List
                  dataSource={recentAlerts}
                  renderItem={(a) => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <Space size={10} wrap>
                            <Tag color="red">⚠️</Tag>
                            <Typography.Text strong>
                              ⭐ {a.star_level ?? a.star ?? a.level ?? '—'}
                            </Typography.Text>
                            <Typography.Text type="secondary">
                              {a.chat_title || a.client_chat_title || a.title || (a.chat_id ? `chat_id: ${a.chat_id}` : '')}
                            </Typography.Text>
                          </Space>
                        }
                        description={
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {fmtShortDate(a.ts || a.created_ts || a.incoming_ts)}
                            </Typography.Text>
                            <div style={{ marginTop: 4 }}>
                              <Typography.Text>
                                {(a.text || a.incoming_text || '').toString().slice(0, 140)}
                                {(a.text || a.incoming_text || '').toString().length > 140 ? '…' : ''}
                              </Typography.Text>
                            </div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </Col>
        </Row>

        <Card>
          <Row gutter={[12, 12]} align="middle" justify="space-between">
            <Col>
              <Typography.Text type="secondary">
                Подсказка: если видите нули — проверьте, что API отдаёт /api/dashboard (и что Mini App открывается внутри Telegram).
              </Typography.Text>
            </Col>
            <Col>
              <Space>
                <Tag color="green">API: /api/health</Tag>
                <Tag color="blue">Dashboard: /api/dashboard</Tag>
              </Space>
            </Col>
          </Row>
        </Card>
      </Space>
    </div>
  );
}